import { useState, useCallback, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { leadsApi, listingsApi } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '@/components/base/ErrorBoundary';

interface Lead {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  source: string;
  stage: string;
  status?: string;
  priority: string;
  budget: string | null;
  requirement: string | null;
  city: string | null;
  assigned_rm: string | null;
  score: number;
  property_interest: string | null;
  listing_title?: string | null;
  deal_value: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface LeadStats {
  total: number;
  new_count: number;
  qualified_count: number;
  converted_count: number;
  avg_score: number;
  total_pipeline_value: number;
}

interface PropertyOption {
  id: number;
  title: string;
  city: string | null;
}

const pipelineStages = ['new', 'contacted', 'qualified', 'site_visit', 'negotiation', 'converted', 'lost'];

const stageLabels: Record<string, string> = {
  new: 'New Lead',
  contacted: 'Contacted',
  qualified: 'Qualified',
  site_visit: 'Site Visit',
  negotiation: 'Negotiation',
  converted: 'Converted',
  lost: 'Lost',
  captured: 'Captured',
};

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    new: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
    contacted: 'bg-accent-100 text-accent-700 border-accent-500/25',
    qualified: 'bg-teal-500/15 text-teal-400 border-teal-500/25',
    site_visit: 'bg-secondary-100 text-secondary-700 border-secondary-200',
    negotiation: 'bg-primary-100 text-primary-700 border-primary-200',
    converted: 'bg-green-500/15 text-green-400 border-green-500/25',
    lost: 'bg-rose-500/15 text-rose-400 border-rose-500/25',
    captured: 'bg-background-200 text-foreground-500 border-background-300/30',
  };
  return map[status] || 'bg-background-200/50 text-foreground-500 border-background-300/30';
};

const statusSelectClass = (status: string) => {
  const map: Record<string, string> = {
    new: 'bg-blue-500/10 border-blue-500/30 text-blue-500',
    contacted: 'bg-accent-50 border-accent-200 text-accent-700',
    qualified: 'bg-teal-500/10 border-teal-500/30 text-teal-500',
    site_visit: 'bg-secondary-50 border-secondary-200 text-secondary-700',
    negotiation: 'bg-primary-50 border-primary-200 text-primary-700',
    converted: 'bg-green-500/10 border-green-500/30 text-green-500',
    lost: 'bg-rose-500/10 border-rose-500/30 text-rose-500',
    captured: 'bg-background-100 border-background-300/40 text-foreground-600',
  };
  return map[status] || 'bg-white border-background-200 text-foreground-700';
};

const sourceOptions = [
  { value: 'website', label: 'Website' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'referral', label: 'Referral' },
  { value: 'social', label: 'Social Media' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'meta_ads', label: 'Meta Ads' },
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'walkin', label: 'Walk-in' },
  { value: 'broker', label: 'Broker' },
];

const typeOptions = [
  { value: 'owner', label: 'Owner' },
  { value: 'land_owner', label: 'Land Owner' },
  { value: 'renter', label: 'Renter' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'agent', label: 'Agent' },
  { value: 'investor', label: 'Investor' },
  { value: 'general_enquiry', label: 'General Enquiry' },
  { value: 'other', label: 'Other' },
];

const budgetOptions = ['Under 10L', '10L-25L', '25L-50L', '50L-1 Cr', '1-2 Cr', '2 Cr+'];

const sortOptions = [
  { key: 'name', label: 'Name' },
  { key: 'date', label: 'Date' },
  { key: 'score', label: 'Score' },
  { key: 'value', label: 'Value' },
];

const sourceLabel = (s: string) => sourceOptions.find((o) => o.value === s)?.label || s || '-';

const sourceIcon = (s: string) => {
  switch (s) {
    case 'whatsapp': return 'ri-whatsapp-line';
    case 'phone_call': return 'ri-phone-line';
    case 'email': return 'ri-mail-line';
    case 'social': return 'ri-share-line';
    case 'referral': return 'ri-group-line';
    case 'walkin': return 'ri-user-line';
    case 'broker': return 'ri-briefcase-line';
    default: return 'ri-global-line';
  }
};

function formatDate(d: string) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatINR(val: number | string): string {
  const n = Number(val) || 0;
  if (n >= 10000000) return '\u20B9' + (n / 10000000).toFixed(1) + 'Cr';
  if (n >= 100000) return '\u20B9' + (n / 100000).toFixed(1) + 'L';
  if (n >= 1000) return '\u20B9' + (n / 1000).toFixed(0) + 'K';
  return '\u20B9' + n.toFixed(0);
}

const emptyLeadForm = {
  name: '',
  email: '',
  phone: '',
  source: 'website',
  message: '',
  propertyInterest: '',
  customProperty: '',
  budgetRange: 'Under 10L',
  timeline: '3-6 months',
};

const emptyEditForm = {
  name: '',
  email: '',
  phone: '',
  source: 'website',
  budgetRange: '',
  city: '',
  propertyInterest: '',
  customProperty: '',
  priority: 'warm',
  notes: '',
};

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leadList, setLeadList] = useState<Lead[]>([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Property options for the Add Lead "Property Interest" dropdown
  const [propertyListings, setPropertyListings] = useState<PropertyOption[]>([]);
  const [listingsLoading, setListingsLoading] = useState(false);

  // Filters & sort
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterBudget, setFilterBudget] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortAsc, setSortAsc] = useState(false);

  // Selection & modal state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [statusNotes, setStatusNotes] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Lead | null>(null);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyLeadForm);
  const [showEdit, setShowEdit] = useState(false);
  const [editTarget, setEditTarget] = useState<Lead | null>(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [updatingStatus, setUpdatingStatus] = useState<Set<number>>(new Set());

  const clearMsg = useCallback(() => setTimeout(() => setActionMsg(null), 3000), []);

  const fetchStats = useCallback(() => {
    leadsApi.stats()
      .then((r) => {
        const d = (r.data || {}) as Record<string, unknown>;
        setStats({
          total: Number(d.total ?? 0),
          new_count: Number(d.new_count ?? 0),
          qualified_count: Number(d.qualified_count ?? 0),
          converted_count: Number(d.converted_count ?? 0),
          avg_score: Number(d.avg_score ?? 0),
          total_pipeline_value: Number(d.total_pipeline_value ?? 0),
        });
      })
      .catch(() => { /* keep previous stats on error */ });
  }, []);

  const fetchLeads = useCallback(async () => {
    setApiLoading(true);
    setLoadError(null);
    try {
      const resp = await leadsApi.list({
        page,
        per_page: perPage,
        search: searchQuery || undefined,
        source: filterSource || undefined,
        stage: filterStatus || undefined,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined,
        budget: filterBudget || undefined,
        type: filterType || undefined,
        sort: sortBy,
        dir: sortAsc ? 'asc' : 'desc',
      });
      const apiResp = resp as unknown as {
        data: Lead[];
        pagination: { total: number; totalPages: number };
      };
      const rows = apiResp?.data || [];
      const normalised = rows.map((l) => ({
        ...l,
        stage: l.stage || l.status || 'new',
        property_interest: l.property_interest || l.listing_title || null,
      }));
      setLeadList(normalised);
      const nextTotal = apiResp?.pagination?.total ?? 0;
      const nextTotalPages = apiResp?.pagination?.totalPages ?? 1;
      setTotal(nextTotal);
      setTotalPages(nextTotalPages);
      if (nextTotalPages > 0 && page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unable to load leads';
      setLoadError(msg);
      console.error('Leads fetch failed:', err);
    } finally {
      setApiLoading(false);
    }
  }, [page, perPage, searchQuery, filterSource, filterStatus, filterDateFrom, filterDateTo, filterBudget, filterType, sortBy, sortAsc]);

  const fetchPropertyListings = useCallback(() => {
    setListingsLoading(true);
    listingsApi.list({ per_page: 500, status: 'available' })
      .then((resp) => {
        const r = resp as unknown as { data: PropertyOption[] };
        setPropertyListings(r?.data || []);
      })
      .catch(() => setPropertyListings([]))
      .finally(() => setListingsLoading(false));
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (showCreate || showEdit) fetchPropertyListings();
  }, [showCreate, showEdit, fetchPropertyListings]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applyFilter = (setter: (value: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };

  const loading = apiLoading;
  const totalLeads = total;
  const newLeads = stats?.new_count ?? 0;
  const qualified = stats?.qualified_count ?? 0;
  const converted = stats?.converted_count ?? 0;
  const avgScore = Math.round(stats?.avg_score ?? 0);
  const pipelineValue = stats?.total_pipeline_value ?? 0;

  const hasActiveFilters = Boolean(filterSource || filterStatus || filterDateFrom || filterDateTo || filterBudget || filterType || searchInput);

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setFilterSource('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterBudget('');
    setFilterType('');
    setPage(1);
  };

  const startItem = total === 0 ? 0 : (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, total);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === leadList.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leadList.map((l) => l.id)));
    }
  };

  const handleBulkStatusUpdate = useCallback(async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    let updated = 0;
    for (const id of Array.from(selectedIds)) {
      try { await leadsApi.updateStage(id, bulkAction); updated++; } catch { /* skip */ }
    }
    setActionMsg({ type: 'success', text: `${updated} leads updated to "${stageLabels[bulkAction] || bulkAction}".` });
    setSelectedIds(new Set());
    setBulkAction('');
    fetchLeads();
    fetchStats();
    clearMsg();
  }, [bulkAction, selectedIds, fetchLeads, fetchStats, clearMsg]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    let count = 0;
    for (const id of Array.from(selectedIds)) {
      try { await leadsApi.delete(id); count++; } catch { /* skip */ }
    }
    setActionMsg({ type: 'success', text: `${count} leads deleted.` });
    setSelectedIds(new Set());
    fetchLeads();
    fetchStats();
    clearMsg();
  }, [selectedIds, fetchLeads, fetchStats, clearMsg]);

  const handleExportCSV = useCallback(() => {
    const headers = [
      'Name', 'Email', 'Phone', 'Source', 'Property Interest', 'Budget Range',
      'Timeline', 'Customer Type', 'Status', 'Score', 'Estimated Value (INR)',
      'Assigned RM', 'Created Date', 'Days in Stage', 'Activities Count',
      'Activity Details',
    ];

    const escapeCSV = (val: unknown): string => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const rows = leadList.map((lead) => [
      escapeCSV(lead.name),
      escapeCSV(lead.email || ''),
      escapeCSV(lead.phone || ''),
      escapeCSV(lead.source || ''),
      escapeCSV(lead.property_interest || ''),
      escapeCSV(lead.budget || ''),
      escapeCSV(''),
      escapeCSV(''),
      escapeCSV(lead.stage || ''),
      escapeCSV(lead.score || 0),
      escapeCSV(lead.deal_value || 0),
      escapeCSV(lead.assigned_rm || ''),
      escapeCSV(lead.created_at || ''),
      escapeCSV(''),
      escapeCSV(0),
      escapeCSV(''),
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `leads-export-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setActionMsg({ type: 'success', text: `Exported ${leadList.length} leads from page ${page} as CSV.` });
    clearMsg();
  }, [leadList, page, clearMsg]);

  const handleStatusUpdate = useCallback(async (leadId: number, newStatus: string) => {
    setSubmitting(true);
    try {
      await leadsApi.updateStage(leadId, newStatus, statusNotes || undefined);
      setActionMsg({ type: 'success', text: `Status updated to "${stageLabels[newStatus] || newStatus}".` });
      setStatusNotes('');
      setSelectedLead((prev) => (prev && prev.id === leadId ? { ...prev, stage: newStatus } : prev));
      fetchLeads();
      fetchStats();
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Update failed.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [statusNotes, fetchLeads, fetchStats, clearMsg]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await leadsApi.delete(deleteTarget.id);
      setActionMsg({ type: 'success', text: `Lead "${deleteTarget.name}" deleted.` });
      setDeleteTarget(null);
      setSelectedLead(null);
      fetchLeads();
      fetchStats();
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Delete failed.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [deleteTarget, fetchLeads, fetchStats, clearMsg]);

  const handleCreateLead = useCallback(async () => {
    if (!createForm.name) {
      setActionMsg({ type: 'error', text: 'Name is required.' });
      clearMsg();
      return;
    }
    const propertyInterest = createForm.propertyInterest === '__other__'
      ? (createForm.customProperty || undefined)
      : (createForm.propertyInterest || undefined);

    setSubmitting(true);
    try {
      await leadsApi.create({
        name: createForm.name,
        email: createForm.email || undefined,
        phone: createForm.phone || undefined,
        source: createForm.source || 'website',
        budget: createForm.budgetRange || undefined,
        property_interest: propertyInterest,
        notes: createForm.message || undefined,
      });
      setActionMsg({ type: 'success', text: `Lead "${createForm.name}" created.` });
      setShowCreate(false);
      setCreateForm(emptyLeadForm);
      setPage(1);
      fetchLeads();
      fetchStats();
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to create lead.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [createForm, fetchLeads, fetchStats, clearMsg]);

  const openEditLead = useCallback((lead: Lead) => {
    setEditTarget(lead);
    setEditForm({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      source: lead.source || 'website',
      budgetRange: lead.budget || '',
      city: lead.city || '',
      propertyInterest: lead.property_interest || '',
      customProperty: '',
      priority: lead.priority || 'warm',
      notes: lead.notes || '',
    });
    setShowEdit(true);
  }, []);

  const handleInlineStatusChange = useCallback(async (leadId: number, newStatus: string) => {
    setUpdatingStatus(prev => new Set(prev).add(leadId));
    setLeadList(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStatus } : l));
    try {
      await leadsApi.updateStage(leadId, newStatus);
      setActionMsg({ type: 'success', text: `Status updated to "${stageLabels[newStatus] || newStatus}".` });
      fetchStats();
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Update failed.' });
      fetchLeads();
    } finally {
      setUpdatingStatus(prev => { const next = new Set(prev); next.delete(leadId); return next; });
      clearMsg();
    }
  }, [fetchLeads, fetchStats, clearMsg]);

  const handleEditSubmit = useCallback(async () => {
    if (!editTarget) return;
    if (!editForm.name) {
      setActionMsg({ type: 'error', text: 'Name is required.' });
      clearMsg();
      return;
    }
    const propertyInterest = editForm.propertyInterest === '__other__'
      ? (editForm.customProperty || undefined)
      : (editForm.propertyInterest || undefined);
    setSubmitting(true);
    try {
      await leadsApi.update(editTarget.id, {
        name: editForm.name,
        email: editForm.email || undefined,
        phone: editForm.phone || undefined,
        source: editForm.source || 'website',
        budget: editForm.budgetRange || undefined,
        city: editForm.city || undefined,
        priority: editForm.priority || 'warm',
        property_interest: propertyInterest,
        notes: editForm.notes || undefined,
      });
      setActionMsg({ type: 'success', text: `Lead "${editForm.name}" updated.` });
      setShowEdit(false);
      setEditTarget(null);
      fetchLeads();
      fetchStats();
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update lead.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [editTarget, editForm, fetchLeads, fetchStats, clearMsg]);

  const scoreColor = (score: number) => {
    if (score >= 75) return 'bg-teal-500';
    if (score >= 50) return 'bg-accent-500';
    if (score >= 30) return 'bg-secondary-500';
    return 'bg-rose-500';
  };

  const scoreTextColor = (score: number) => {
    if (score >= 75) return 'text-teal-400';
    if (score >= 50) return 'text-accent-700';
    if (score >= 30) return 'text-secondary-700';
    return 'text-rose-400';
  };

  return (
    <DashboardLayout>
      <ErrorBoundary>
        <div className="space-y-5">
          {actionMsg && (
            <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
              {actionMsg.text}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="font-heading text-xl font-bold text-foreground-950">Leads</h1>
              <p className="text-xs text-foreground-500">
                {loading ? 'Loading...' : `${totalLeads} leads · Avg Score: ${avgScore} · Pipeline Value: ${formatINR(pipelineValue)}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className="px-3 py-2 rounded-xl bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer"
                  >
                    <option value="">Bulk Action</option>
                    {Object.entries(stageLabels).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleBulkStatusUpdate}
                    disabled={!bulkAction}
                    className="px-3 py-2 rounded-xl bg-accent-500 text-white text-xs font-semibold hover:bg-accent-400 disabled:opacity-40 cursor-pointer whitespace-nowrap"
                  >
                    Apply
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="px-3 py-2 rounded-xl bg-rose-500/10 text-rose-400 text-xs font-semibold hover:bg-rose-500/20 cursor-pointer whitespace-nowrap"
                  >
                    Delete ({selectedIds.size})
                  </button>
                </div>
              )}
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 hover:border-accent-200 cursor-pointer whitespace-nowrap transition-all"
                title="Download the current page of leads as CSV"
              >
                <i className="ri-download-2-line" /> Export CSV
              </button>
              <button
                onClick={() => { setCreateForm(emptyLeadForm); setShowCreate(true); }}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line" /> Add Lead
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Total Leads', value: totalLeads.toString(), icon: 'ri-user-line', color: 'accent' },
              { label: 'New', value: newLeads.toString(), icon: 'ri-sparkling-line', color: 'blue' },
              { label: 'Qualified', value: qualified.toString(), icon: 'ri-verified-badge-line', color: 'teal' },
              { label: 'Converted', value: converted.toString(), icon: 'ri-trophy-line', color: 'green' },
              { label: 'Avg Score', value: avgScore.toString(), icon: 'ri-bar-chart-line', color: 'primary' },
            ].map((stat) => (
              <div key={stat.label} className="rounded-xl border bg-white border-background-200/60 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    stat.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                    stat.color === 'blue' ? 'bg-blue-500/15 text-blue-400' :
                    stat.color === 'teal' ? 'bg-teal-500/15 text-teal-400' :
                    stat.color === 'green' ? 'bg-green-500/15 text-green-400' :
                    'bg-primary-500/15 text-primary-700'
                  }`}><i className={`${stat.icon} text-xs`} /></span>
                  <span className="text-[10px] text-foreground-500 uppercase tracking-wider">{stat.label}</span>
                </div>
                <p className="font-heading text-lg font-bold text-foreground-950">{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Filters & Sort */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-background-400 text-sm" />
              <input
                type="text"
                placeholder="Search by name, email, property..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50"
              />
            </div>
            <select
              value={filterSource}
              onChange={(e) => applyFilter(setFilterSource, e.target.value)}
              className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer"
            >
              <option value="">All Sources</option>
              {sourceOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => applyFilter(setFilterStatus, e.target.value)}
              className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer"
            >
              <option value="">All Stages</option>
              {Object.entries(stageLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-2.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                hasActiveFilters || showFilters
                  ? 'bg-accent-100 text-accent-700 border border-accent-200'
                  : 'bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950'
              }`}
            >
              <i className="ri-filter-3-line text-[11px]" />
              Filters
              {hasActiveFilters && (
                <span className="w-4 h-4 rounded-full bg-accent-500 text-white text-[9px] flex items-center justify-center font-bold">
                  {[filterSource, filterStatus, filterDateFrom, filterBudget, filterType, searchInput].filter(Boolean).length}
                </span>
              )}
            </button>
            <div className="flex items-center gap-2 ml-auto flex-shrink-0">
              <span className="text-[10px] text-foreground-500 uppercase tracking-wider">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                className="px-2.5 py-1.5 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer"
              >
                {sortOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <button
                onClick={() => { setSortAsc((v) => !v); setPage(1); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer transition-colors"
                title={sortAsc ? 'Ascending' : 'Descending'}
              >
                <i className={`text-xs ${sortAsc ? 'ri-sort-asc' : 'ri-sort-desc'}`} />
              </button>
            </div>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="rounded-xl border border-accent-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground-950">Advanced Filters</span>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-[10px] text-rose-400 hover:text-rose-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-close-line" /> Clear all
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Date From</label>
                  <input type="date" value={filterDateFrom} onChange={(e) => applyFilter(setFilterDateFrom, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Date To</label>
                  <input type="date" value={filterDateTo} onChange={(e) => applyFilter(setFilterDateTo, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Budget Range</label>
                  <select value={filterBudget} onChange={(e) => applyFilter(setFilterBudget, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer">
                    <option value="">All Budgets</option>
                    {budgetOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Customer Type</label>
                  <select value={filterType} onChange={(e) => applyFilter(setFilterType, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer">
                    <option value="">All Types</option>
                    {typeOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              {hasActiveFilters && (
                <p className="text-[10px] text-foreground-500">
                  Page <span className="text-foreground-950 font-semibold">{page}</span> of {totalPages} · {total} total leads
                </p>
              )}
            </div>
          )}

          {/* Leads Table */}
          <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
            {loading ? (
              <div className="p-12 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 bg-background-200/60 rounded-lg animate-pulse" />)}
              </div>
            ) : loadError ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                  <i className="ri-error-warning-line text-rose-400 text-xl" />
                </div>
                <p className="text-sm font-semibold text-foreground-950 mb-1">Failed to load leads</p>
                <p className="text-xs text-foreground-500 mb-4 max-w-md mx-auto">{loadError}</p>
                <button
                  onClick={fetchLeads}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" /> Retry
                </button>
              </div>
            ) : leadList.length === 0 ? (
              <div className="p-12 text-center">
                <i className="ri-inbox-line text-3xl text-foreground-500 mb-2 block" />
                <p className="text-sm text-foreground-500">{hasActiveFilters ? 'No leads match your filters.' : 'No leads yet.'}</p>
                {hasActiveFilters && <button onClick={clearFilters} className="mt-2 text-xs text-accent-700 hover:text-accent-300 cursor-pointer">Clear filters</button>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/60">
                      <th className="text-left px-4 py-3 w-10">
                        <input type="checkbox" checked={selectedIds.size === leadList.length && leadList.length > 0} onChange={toggleAll} className="w-4 h-4 rounded cursor-pointer" />
                      </th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Lead</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Source</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Interest</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Score</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Value</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">City</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">RM</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Date</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadList.map((lead) => (
                      <tr key={lead.id} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors">
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedIds.has(lead.id)} onChange={() => toggleSelect(lead.id)} className="w-4 h-4 rounded cursor-pointer" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-bold text-foreground-950">{lead.name.split(' ').map((n: string) => n[0]).join('')}</span>
                            </div>
                            <div className="min-w-0">
                              <button onClick={() => navigate(`/dashboard/leads/${lead.id}`)} className="text-sm font-medium text-foreground-950 hover:text-accent-700 cursor-pointer whitespace-nowrap transition-colors">{lead.name}</button>
                              <p className="text-[11px] text-foreground-500">{lead.email || '-'}</p>
                              {lead.phone && (
                                <p className="text-[11px] text-foreground-500 flex items-center gap-1">
                                  <i className="ri-phone-line text-[9px] text-accent-700" />
                                  {lead.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                            <i className={`${sourceIcon(lead.source || '')} text-accent-700`} />
                            {sourceLabel(lead.source || '')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-foreground-500 max-w-[180px] truncate">{lead.property_interest || '-'}</td>
                        <td className="px-4 py-3">
                          <select
                            value={lead.stage || 'new'}
                            onChange={(e) => handleInlineStatusChange(lead.id, e.target.value)}
                            disabled={updatingStatus.has(lead.id)}
                            className={`w-full min-w-[130px] px-2 py-1.5 rounded-lg border text-[11px] font-semibold cursor-pointer focus:outline-none focus:ring-1 focus:ring-accent-300 disabled:opacity-50 disabled:cursor-not-allowed ${statusSelectClass(lead.stage || 'new')}`}
                          >
                            {Object.entries(stageLabels).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${scoreColor(lead.score || 0)}`} style={{ width: `${lead.score || 0}%` }} />
                            </div>
                            <span className={`text-[10px] font-semibold ${scoreTextColor(lead.score || 0)}`}>{lead.score || 0}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-semibold text-foreground-950 whitespace-nowrap">{formatINR(lead.deal_value || 0)}</td>
                        <td className="px-4 py-3 text-xs text-foreground-500">{lead.city || '-'}</td>
                        <td className="px-4 py-3 text-xs text-foreground-500">{lead.assigned_rm || '-'}</td>
                        <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(lead.created_at || '')}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => { setSelectedLead(lead); setStatusNotes(''); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="View"><i className="ri-eye-line" /></button>
                            <button onClick={() => openEditLead(lead)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-500/10 text-foreground-500 hover:text-accent-700 cursor-pointer" title="Edit"><i className="ri-edit-line" /></button>
                            <button onClick={() => setDeleteTarget(lead)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-rose-500/10 text-foreground-500 hover:text-rose-400 cursor-pointer" title="Delete"><i className="ri-delete-bin-line" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && !loadError && total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 rounded-xl border border-background-200/60 bg-white px-4 py-3">
              <p className="text-xs text-foreground-500">
                Showing <span className="text-foreground-950 font-semibold">{startItem}</span>–<span className="text-foreground-950 font-semibold">{endItem}</span> of <span className="text-foreground-950 font-semibold">{total}</span> leads
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-foreground-500 uppercase tracking-wider">Per page:</span>
                  <select
                    value={perPage}
                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className="px-2 py-1.5 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer"
                  >
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  <span className="px-2 text-xs text-foreground-500 whitespace-nowrap">
                    Page <span className="text-foreground-950 font-semibold">{page}</span> of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lead Detail Slide-out */}
          {selectedLead && (
            <div className="fixed inset-0 z-[1000] flex">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedLead(null)} />
              <div className="relative z-10 ml-auto w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
                <div className="sticky top-0 bg-white border-b border-background-200/60 px-6 py-4 flex items-center justify-between z-10">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">Lead Detail</h2>
                  <button onClick={() => setSelectedLead(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line text-lg" /></button>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-foreground-950">{selectedLead.name.split(' ').map((n: string) => n[0]).join('')}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground-950">{selectedLead.name}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(selectedLead.stage || 'new')}`}>{stageLabels[selectedLead.stage || 'new'] || selectedLead.stage}</span>
                          <span className={`text-[10px] font-semibold ${scoreTextColor(selectedLead.score || 0)}`}>Score: {selectedLead.score || 0}</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Phone</span><a href={`tel:${selectedLead.phone}`} className="text-sm text-accent-700">{selectedLead.phone || '-'}</a></div>
                      <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Email</span><span className="text-sm text-foreground-950">{selectedLead.email || '-'}</span></div>
                      <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Source</span><span className="text-sm text-foreground-950 capitalize">{sourceLabel(selectedLead.source || '')}</span></div>
                      <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Budget</span><span className="text-sm text-foreground-950">{selectedLead.budget || '-'}</span></div>
                      <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">City</span><span className="text-sm text-foreground-950">{selectedLead.city || '-'}</span></div>
                      <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Priority</span><span className="text-sm text-foreground-950 capitalize">{selectedLead.priority || '-'}</span></div>
                    </div>
                    <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Property Interest</span><span className="text-sm text-foreground-950">{selectedLead.property_interest || '-'}</span></div>
                    <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Requirements</span><p className="text-sm text-foreground-500">{selectedLead.requirement || '-'}</p></div>
                    <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Notes</span><p className="text-sm text-foreground-500">{selectedLead.notes || '-'}</p></div>
                    <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Assigned RM</span><span className="text-sm text-foreground-950">{selectedLead.assigned_rm || '-'}</span></div>
                    <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Deal Value</span><span className="text-sm font-bold text-foreground-950">{formatINR(selectedLead.deal_value || 0)}</span></div>
                  </div>

                  <div className="border-t border-background-200/60 pt-5 space-y-4">
                    <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Update Stage</h3>
                    <div className="flex flex-wrap gap-2">
                      {pipelineStages.map((stage) => (
                        <button
                          key={stage}
                          onClick={() => handleStatusUpdate(selectedLead.id, stage)}
                          disabled={submitting}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                            selectedLead.stage === stage
                              ? 'bg-accent-100 text-accent-700 border-accent-200'
                              : 'border-background-200/60 text-foreground-500 hover:text-foreground-950 hover:border-accent-200 disabled:opacity-40'
                          }`}
                        >
                          {stageLabels[stage]}
                        </button>
                      ))}
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Add Note (optional)</label>
                      <textarea
                        rows={2}
                        placeholder="Optional note for this status change..."
                        value={statusNotes}
                        onChange={(e) => setStatusNotes(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none"
                      />
                    </div>
                  </div>

                  <div className="border-t border-background-200/60 pt-5">
                    <button
                      onClick={() => setDeleteTarget(selectedLead)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-rose-500/20 text-xs text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line" /> Delete Lead
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {deleteTarget && (
            <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
              <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                    <i className="ri-delete-bin-line text-rose-400 text-xl" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">Delete Lead?</h3>
                  <p className="text-sm text-foreground-500 mt-1">This will permanently delete <strong className="text-foreground-950">{deleteTarget.name}</strong>. This cannot be undone.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 cursor-pointer whitespace-nowrap">Delete</button>
                </div>
              </div>
            </div>
          )}

          {/* Create Lead Modal */}
          {showCreate && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
              <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">Add Lead</h2>
                  <button onClick={() => setShowCreate(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line" /></button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Name *</label>
                    <input type="text" placeholder="Full name" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Email</label>
                    <input type="email" placeholder="Email address" value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Phone</label>
                      <input type="text" placeholder="Phone" value={createForm.phone} onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Source</label>
                      <select value={createForm.source} onChange={(e) => setCreateForm((f) => ({ ...f, source: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        {sourceOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Budget Range</label>
                      <select value={createForm.budgetRange} onChange={(e) => setCreateForm((f) => ({ ...f, budgetRange: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        {budgetOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Timeline</label>
                      <select value={createForm.timeline} onChange={(e) => setCreateForm((f) => ({ ...f, timeline: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        {['Within 2 weeks', 'Within 1 month', '1-3 months', '3-6 months'].map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Property Interest</label>
                    {listingsLoading ? (
                      <div className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-500">Loading properties...</div>
                    ) : (
                      <>
                        <select value={createForm.propertyInterest} onChange={(e) => setCreateForm((f) => ({ ...f, propertyInterest: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                          <option value="">No property (general)</option>
                          {propertyListings.map((p) => (
                            <option key={p.id} value={p.title}>{p.title}{p.city ? ` — ${p.city}` : ''}</option>
                          ))}
                          <option value="__other__">Other (enter manually)</option>
                        </select>
                        {createForm.propertyInterest === '__other__' && (
                          <input
                            type="text"
                            placeholder="Enter property title"
                            value={createForm.customProperty}
                            onChange={(e) => setCreateForm((f) => ({ ...f, customProperty: e.target.value }))}
                            className="mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950"
                          />
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Notes</label>
                    <textarea rows={2} placeholder="Additional notes..." value={createForm.message} onChange={(e) => setCreateForm((f) => ({ ...f, message: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none" />
                  </div>
                  <button onClick={handleCreateLead} disabled={submitting} className="w-full py-3 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                    {submitting ? 'Saving...' : 'Save Lead'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Lead Modal */}
          {showEdit && editTarget && (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowEdit(false); setEditTarget(null); }} />
              <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">Edit Lead</h2>
                  <button onClick={() => { setShowEdit(false); setEditTarget(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line" /></button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Name *</label>
                    <input type="text" placeholder="Full name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Email</label>
                      <input type="email" placeholder="Email address" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Phone</label>
                      <input type="text" placeholder="Phone" value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Source</label>
                      <select value={editForm.source} onChange={(e) => setEditForm((f) => ({ ...f, source: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        {sourceOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Priority</label>
                      <select value={editForm.priority} onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        <option value="hot">Hot</option>
                        <option value="warm">Warm</option>
                        <option value="cold">Cold</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Budget Range</label>
                      <select value={editForm.budgetRange} onChange={(e) => setEditForm((f) => ({ ...f, budgetRange: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                        <option value="">Not specified</option>
                        {budgetOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">City</label>
                      <input type="text" placeholder="City" value={editForm.city} onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Property Interest</label>
                    {listingsLoading ? (
                      <div className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-500">Loading properties...</div>
                    ) : (
                      <>
                        <select value={editForm.propertyInterest} onChange={(e) => setEditForm((f) => ({ ...f, propertyInterest: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                          <option value="">No property (general)</option>
                          {editForm.propertyInterest && editForm.propertyInterest !== '__other__' && !propertyListings.some((p) => p.title === editForm.propertyInterest) && (
                            <option value={editForm.propertyInterest}>{editForm.propertyInterest} (current)</option>
                          )}
                          {propertyListings.map((p) => (
                            <option key={p.id} value={p.title}>{p.title}{p.city ? ` — ${p.city}` : ''}</option>
                          ))}
                          <option value="__other__">Other (enter manually)</option>
                        </select>
                        {editForm.propertyInterest === '__other__' && (
                          <input
                            type="text"
                            placeholder="Enter property title"
                            value={editForm.customProperty}
                            onChange={(e) => setEditForm((f) => ({ ...f, customProperty: e.target.value }))}
                            className="mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950"
                          />
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Notes</label>
                    <textarea rows={3} placeholder="Additional notes..." value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none" />
                  </div>
                  <button onClick={handleEditSubmit} disabled={submitting} className="w-full py-3 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </ErrorBoundary>
    </DashboardLayout>
  );
}